import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthGuard from "./components/AuthGuard";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import { Navigate } from "react-router-dom";

// Lazy-loaded routes (code-split to shrink initial bundle)
const Report = lazy(() => import("./pages/Report"));
const Analyse = lazy(() => import("./pages/Analyse"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const LeadMagnet = lazy(() => import("./pages/LeadMagnet"));
const Faq = lazy(() => import("./pages/Faq"));
const About = lazy(() => import("./pages/About"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminOverview = lazy(() => import("./pages/AdminOverview"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminAudits = lazy(() => import("./pages/AdminAudits"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminSubscribers = lazy(() => import("./pages/AdminSubscribers"));
const AdminInviteCodes = lazy(() => import("./pages/AdminInviteCodes"));
const AdminScans = lazy(() => import("./pages/AdminScans"));
const AdminScanDetail = lazy(() => import("./pages/AdminScanDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const ContactExpert = lazy(() => import("./pages/ContactExpert"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PlanActionNlpd = lazy(() => import("./pages/PlanActionNlpd"));
const ReportIssue = lazy(() => import("./pages/ReportIssue"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const AuditPro = lazy(() => import("./pages/AuditPro"));
const AuditProSession = lazy(() => import("./pages/AuditProSession"));
const AuditProQuestionnaire = lazy(() => import("./pages/AuditProQuestionnaire"));
const AuditProRapport = lazy(() => import("./pages/AuditProRapport"));
const AuditProRapportPrint = lazy(() => import("./pages/AuditProRapportPrint"));
const AcheterAuditPro = lazy(() => import("./pages/AcheterAuditPro"));
const AcheterAuditProSuccess = lazy(() => import("./pages/AcheterAuditProSuccess"));
const AcheterAuditProCancel = lazy(() => import("./pages/AcheterAuditProCancel"));
const QaProBypass = lazy(() => import("./pages/QaProBypass"));

// STRICTLY-GATED QA-only Stripe bypass. Compiled-in only when the build-time
// flag VITE_PRO_QA_BYPASS === '1' (see .env.example). URL-only, no nav link.
const QA_PRO_BYPASS_ENABLED = import.meta.env.VITE_PRO_QA_BYPASS === "1";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            {/* /resultats route retired 2026-06-16 — the legacy Results.tsx
                page was the URL-flow result UI that predates AuditResults.tsx.
                The Mistral migration retired its inputs (URL-only flow); the
                active result UI is /analyse/:reportId. */}
            <Route path="/resultats" element={<Navigate to="/" replace />} />
            <Route path="/analyse/:reportId" element={<Analyse />} />
            <Route path="/report/:id" element={<Report />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/guide-nlpd" element={<LeadMagnet />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/admin" element={<ProtectedRoute><AdminOverview /></ProtectedRoute>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/audits" element={<ProtectedRoute><AdminAudits /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
            <Route path="/admin/abonnes" element={<ProtectedRoute><AdminSubscribers /></ProtectedRoute>} />
            <Route path="/admin/invites" element={<ProtectedRoute><AdminInviteCodes /></ProtectedRoute>} />
            <Route path="/admin/scans" element={<ProtectedRoute><AdminScans /></ProtectedRoute>} />
            <Route path="/admin/scans/:kind/:id" element={<ProtectedRoute><AdminScanDetail /></ProtectedRoute>} />
            <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/contact-expert" element={<ContactExpert />} />
            {/* /contact never existed as a page; visitors and old links land
                there anyway (2026-07-30 site walkthrough) — forward them. */}
            <Route path="/contact" element={<Navigate to="/contact-expert" replace />} />
            <Route path="/desinscription" element={<Unsubscribe />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/outils/plan-action-nlpd" element={<PlanActionNlpd />} />
            <Route path="/signaler-un-probleme" element={<ReportIssue />} />

            {/* Pro Audit */}
            <Route path="/login" element={<Login />} />
            <Route path="/acheter-diagnostic-pro" element={<AcheterAuditPro />} />
            <Route path="/acheter-diagnostic-pro/success" element={<AcheterAuditProSuccess />} />
            <Route path="/acheter-diagnostic-pro/cancel" element={<AcheterAuditProCancel />} />
            <Route path="/audit-pro" element={<AuthGuard><AuditPro /></AuthGuard>} />
            <Route path="/audit-pro/:sessionId" element={<AuthGuard><AuditProSession /></AuthGuard>} />
            <Route path="/audit-pro/:sessionId/questionnaire" element={<AuthGuard><AuditProQuestionnaire /></AuthGuard>} />
            <Route path="/audit-pro/:sessionId/questionnaire/:step" element={<AuthGuard><AuditProQuestionnaire /></AuthGuard>} />
            <Route path="/audit-pro/:sessionId/rapport" element={<AuthGuard><AuditProRapport /></AuthGuard>} />
            <Route path="/audit-pro/:sessionId/rapport/print" element={<AuthGuard><AuditProRapportPrint /></AuthGuard>} />

            {/* QA-only Pro Stripe bypass — defence in depth: (1) build-flag gate
                (outermost, so a normal prod build excludes the route entirely →
                bounces home), (2) ProtectedRoute (session + is_admin RPC, same
                gate as /admin/*), then server secret + server domain allowlist. */}
            <Route
              path="/qa/pro-bypass"
              element={
                QA_PRO_BYPASS_ENABLED ? (
                  <ProtectedRoute>
                    <QaProBypass />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CookieBanner />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
